import React from 'react'
import { Link } from 'react-router-dom'
import styles from './MicrositePageHeader.module.css'
import { Save, History, ChevronLeft, Trash2 } from 'lucide-react'

/**
 * MicrositePageHeader
 *
 * Shared header used at the top of nearly every microsite screen —
 * title, subtitle, optional back breadcrumb, and a right-hand action
 * cluster (Save / History / Delete / custom buttons).
 *
 * Usage (settings screen with save + history):
 *   <MicrositePageHeader
 *     title="Theme"
 *     subtitle="Colors, fonts and branding for your microsite."
 *     isDirty={isDirty}
 *     saving={saving}
 *     onSave={handleSave}
 *     onHistory={() => setHistoryOpen(true)}
 *   />
 *
 * Usage (editor screen with back + delete):
 *   <MicrositePageHeader
 *     title="Edit blog post"
 *     backTo="/microsite/blog"
 *     backLabel="Blog"
 *     isDirty={isDirty}
 *     saving={saving}
 *     onSave={handleSave}
 *     onDelete={handleDelete}
 *   />
 *
 * Usage (list screen with a primary action button, no save state):
 *   <MicrositePageHeader
 *     title="Custom pages"
 *     subtitle="Pages beyond your standard landing sections."
 *     primaryAction={{ label: 'New page', onClick: () => navigate('/microsite/pages/new') }}
 *   />
 */
const MicrositePageHeader = ({
    title,
    subtitle,
    backTo,
    backLabel,
    isDirty,
    saving,
    onSave,
    onHistory,
    onDelete,
    primaryAction,
    children // extra custom action buttons, rendered before Save
}) => {
    const showSaveCluster = typeof onSave === 'function'

    return (
        <div className={styles.header}>
            <div className={styles.left}>
                {backTo && (
                    <Link to={backTo} className={styles.backLink}>
                        <ChevronLeft size={13} />
                        {backLabel || 'Back'}
                    </Link>
                )}
                <h1 className={styles.title}>{title}</h1>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>

            <div className={styles.actions}>
                {children}

                {onDelete && (
                    <button type="button" className={styles.deleteBtn} onClick={onDelete}>
                        <Trash2 size={14} />
                        Delete
                    </button>
                )}

                {onHistory && (
                    <button type="button" className={styles.historyBtn} onClick={onHistory}>
                        <History size={14} />
                        History
                    </button>
                )}

                {showSaveCluster && (
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={onSave}
                        disabled={!isDirty || saving}
                    >
                        {isDirty && !saving && <span className={styles.unsavedDot} />}
                        <Save size={14} />
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                )}

                {primaryAction && (
                    <button type="button" className={styles.primaryBtn} onClick={primaryAction.onClick}>
                        {primaryAction.icon}
                        {primaryAction.label}
                    </button>
                )}
            </div>
        </div>
    )
}

export default MicrositePageHeader